import { useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import type { Line, Role } from '../api/types'
import { formatTime, parseTime } from '../lib/format'

export type LinePatch = {
  roleId?: string
  start?: number
  end?: number
  text?: string
  /** Оригинал реплики; появляется после автоматического разбора. */
  textOriginal?: string
}

export type LineListProps = {
  lines: Line[]
  roles: Role[]
  takeCountByLine: Record<string, number>
  selectedLineId: string | null
  currentTime: number
  videoDuration: number
  disabled?: boolean
  onSelect: (lineId: string) => void
  onSeek: (time: number) => void
  onCreate: () => void
  onPatch: (lineId: string, patch: LinePatch) => void
  onDelete: (lineId: string) => void
  renderExtra?: (line: Line) => ReactNode
  /**
   * Отмеченные для объединения реплики (docs/SEGMENTATION.md, «Объединение
   * и создание реплик»). Пусто — колонка отметок работает, но панель скрыта.
   */
  mergeSelection?: ReadonlySet<string>
  onToggleMerge?: (lineId: string) => void
  onClearMerge?: () => void
  /** Открыть подтверждение объединения — доступно только для соседних реплик. */
  onMerge?: () => void
  /** Реплика только что создана — фокус уходит прямо в поле текста. */
  autoFocusLineId?: string | null
}

type LineItemProps = {
  line: Line
  role: Role | undefined
  roles: Role[]
  takes: number
  selected: boolean
  /** Отмечена для объединения; undefined — механизм объединения не подключён. */
  merging: boolean | undefined
  onToggleMerge: ((lineId: string) => void) | undefined
  autoFocus: boolean
  currentTime: number
  videoDuration: number
  disabled: boolean
  onSelect: (lineId: string) => void
  onSeek: (time: number) => void
  onPatch: (lineId: string, patch: LinePatch) => void
  onDelete: (lineId: string) => void
  extra: ReactNode
}

function LineItem({
  line,
  role,
  roles,
  takes,
  selected,
  merging,
  onToggleMerge,
  autoFocus,
  currentTime,
  videoDuration,
  disabled,
  onSelect,
  onSeek,
  onPatch,
  onDelete,
  extra,
}: LineItemProps): JSX.Element {
  const [start, setStart] = useState(line.start.toFixed(2))
  const [end, setEnd] = useState(line.end.toFixed(2))
  const [text, setText] = useState(line.text)
  const [original, setOriginal] = useState(line.textOriginal ?? '')
  const [addingOriginal, setAddingOriginal] = useState(false)
  const textRef = useRef<HTMLTextAreaElement | null>(null)

  // Реплику завели протяжкой по таймлайну — текст вводится сразу, без
  // лишнего клика в поле.
  useEffect(() => {
    if (!autoFocus || !selected) return
    const field = textRef.current
    if (!field) return
    field.focus()
    field.scrollIntoView({ block: 'nearest' })
  }, [autoFocus, selected])

  useEffect(() => setStart(line.start.toFixed(2)), [line.start])
  useEffect(() => setEnd(line.end.toFixed(2)), [line.end])
  useEffect(() => setText(line.text), [line.text])
  useEffect(() => setOriginal(line.textOriginal ?? ''), [line.textOriginal])

  const commitStart = (raw: string): void => {
    const parsed = parseTime(raw)
    if (parsed === null || parsed < 0 || parsed >= line.end) {
      setStart(line.start.toFixed(2))
      return
    }
    if (Math.abs(parsed - line.start) < 0.001) return
    onPatch(line.id, { start: parsed })
  }

  const commitEnd = (raw: string): void => {
    const parsed = parseTime(raw)
    const max = videoDuration > 0 ? videoDuration + 1 : Number.POSITIVE_INFINITY
    if (parsed === null || parsed <= line.start || parsed > max) {
      setEnd(line.end.toFixed(2))
      return
    }
    if (Math.abs(parsed - line.end) < 0.001) return
    onPatch(line.id, { end: parsed })
  }

  const commitText = (): void => {
    if (text === line.text) return
    onPatch(line.id, { text })
  }

  const commitOriginal = (): void => {
    if (original === (line.textOriginal ?? '')) return
    onPatch(line.id, { textOriginal: original })
  }

  // Ручная разметка оригиналом не пользуется — не показываем пустое поле зря.
  const hasOriginal = Boolean(line.textOriginal && line.textOriginal.trim())

  const color = role?.color ?? '#5a6577'

  return (
    <div
      className={`line-item${selected ? ' selected' : ''}`}
      style={{ borderLeftColor: color }}
      onClick={() => {
        if (!selected) {
          onSelect(line.id)
          onSeek(line.start)
        }
      }}
    >
      <div className="line-head">
        {onToggleMerge ? (
          <input
            type="checkbox"
            className="line-merge-check"
            checked={merging === true}
            disabled={disabled}
            aria-label="Отметить для объединения"
            title="Отметить для объединения с соседней репликой"
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleMerge(line.id)}
          />
        ) : null}
        <span className="role-swatch" style={{ background: color }} />
        <strong style={{ color: 'var(--text)' }}>{role?.name ?? 'Без роли'}</strong>
        <span className="mono">
          {formatTime(line.start)} → {formatTime(line.end)}
        </span>
        <span className="spacer" />
        {takes > 0 ? (
          <span className="badge" title="Записанных дублей">
            ● {takes}
          </span>
        ) : (
          <span className="faint">нет записи</span>
        )}
      </div>
      <div className={`line-text${line.text ? '' : ' placeholder'}`}>
        {line.text || 'Текст реплики не задан'}
      </div>
      {hasOriginal ? (
        <div className="line-original" title="Оригинальная реплика">
          {line.textOriginal}
        </div>
      ) : null}

      {selected ? (
        <div className="line-edit" onClick={(e) => e.stopPropagation()}>
          <div className="grid">
            <div>
              <label htmlFor={`role-${line.id}`}>Роль</label>
              <select
                id={`role-${line.id}`}
                value={line.roleId}
                disabled={disabled}
                onChange={(e) => onPatch(line.id, { roleId: e.currentTarget.value })}
              >
                <option value="">— без роли —</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`start-${line.id}`}>Начало</label>
              <div className="row">
                <input
                  id={`start-${line.id}`}
                  className="mono"
                  type="text"
                  value={start}
                  disabled={disabled}
                  onChange={(e) => setStart(e.currentTarget.value)}
                  onBlur={(e) => commitStart(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur()
                  }}
                />
                <button
                  type="button"
                  className="ghost small"
                  disabled={disabled}
                  title="Взять текущую позицию видео"
                  onClick={() => commitStart(currentTime.toFixed(2))}
                >
                  ⌖
                </button>
              </div>
            </div>
            <div>
              <label htmlFor={`end-${line.id}`}>Конец</label>
              <div className="row">
                <input
                  id={`end-${line.id}`}
                  className="mono"
                  type="text"
                  value={end}
                  disabled={disabled}
                  onChange={(e) => setEnd(e.currentTarget.value)}
                  onBlur={(e) => commitEnd(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur()
                  }}
                />
                <button
                  type="button"
                  className="ghost small"
                  disabled={disabled}
                  title="Взять текущую позицию видео"
                  onClick={() => commitEnd(currentTime.toFixed(2))}
                >
                  ⌖
                </button>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor={`text-${line.id}`}>
              {hasOriginal || addingOriginal ? 'Перевод — то, что произносит актёр' : 'Текст'}
            </label>
            <textarea
              id={`text-${line.id}`}
              ref={textRef}
              value={text}
              rows={2}
              disabled={disabled}
              placeholder="Что говорит персонаж"
              onChange={(e) => setText(e.currentTarget.value)}
              onBlur={commitText}
            />
          </div>

          {hasOriginal || addingOriginal ? (
            <div>
              <label htmlFor={`original-${line.id}`}>Оригинал</label>
              <textarea
                id={`original-${line.id}`}
                value={original}
                rows={2}
                disabled={disabled}
                placeholder="Что было сказано в видео"
                // Поле не должно исчезать под руками, если оригинал стёрли целиком.
                onFocus={() => setAddingOriginal(true)}
                onChange={(e) => setOriginal(e.currentTarget.value)}
                onBlur={commitOriginal}
              />
              <span className="faint">
                По нему актёр сверяет интонацию, а редактор правит машинный перевод.
              </span>
            </div>
          ) : (
            <div className="row">
              <button
                type="button"
                className="ghost small"
                disabled={disabled}
                onClick={() => setAddingOriginal(true)}
              >
                + Оригинал реплики
              </button>
            </div>
          )}

          {extra}

          <div className="row">
            <button
              type="button"
              className="ghost small"
              disabled={disabled}
              onClick={() => onSeek(Math.max(0, line.start - 1.5))}
            >
              ⏮ К реплике
            </button>
            <span className="spacer" />
            <button
              type="button"
              className="danger small"
              disabled={disabled}
              onClick={() => {
                const question =
                  takes > 0
                    ? `Удалить реплику вместе с ${takes} дублями?`
                    : 'Удалить реплику?'
                if (window.confirm(question)) onDelete(line.id)
              }}
            >
              Удалить реплику
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function LineList({
  lines,
  roles,
  takeCountByLine,
  selectedLineId,
  currentTime,
  videoDuration,
  disabled = false,
  onSelect,
  onSeek,
  onCreate,
  onPatch,
  onDelete,
  renderExtra,
  mergeSelection,
  onToggleMerge,
  onClearMerge,
  onMerge,
  autoFocusLineId = null,
}: LineListProps): JSX.Element {
  const sorted = useMemo(
    () => [...lines].sort((a, b) => a.start - b.start || a.end - b.end),
    [lines],
  )
  const roleById = new Map(roles.map((role) => [role.id, role]))

  const selectedCount = mergeSelection ? mergeSelection.size : 0
  // Объединять можно только идущие подряд: дыра между репликами означала бы,
  // что в одну склеиваются куски из разных мест сцены.
  const adjacent = useMemo(() => {
    if (!mergeSelection || mergeSelection.size < 2) return false
    const indexes: number[] = []
    sorted.forEach((line, index) => {
      if (mergeSelection.has(line.id)) indexes.push(index)
    })
    if (indexes.length !== mergeSelection.size) return false
    const first = indexes[0] as number
    return indexes.every((value, offset) => value === first + offset)
  }, [mergeSelection, sorted])

  return (
    <section className="panel">
      <header>
        <h2>Реплики</h2>
        <span className="faint">{lines.length}</span>
        <button type="button" onClick={onCreate} disabled={disabled}>
          + Реплика с {formatTime(currentTime)}
        </button>
      </header>

      {selectedCount > 0 ? (
        <div className="merge-bar">
          <strong>Выбрано для объединения: {selectedCount}</strong>
          <span className="faint">
            {selectedCount < 2
              ? 'Отметьте ещё одну соседнюю реплику.'
              : adjacent
                ? 'Получится одна реплика: начало первой, конец последней, тексты через пробел.'
                : 'Выбранные реплики идут не подряд — объединять можно только соседние.'}
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="primary small"
            disabled={disabled || !adjacent}
            onClick={onMerge}
          >
            Объединить ({selectedCount})
          </button>
          <button type="button" className="ghost small" onClick={onClearMerge}>
            Сбросить
          </button>
        </div>
      ) : null}
      {sorted.length === 0 ? (
        <div className="empty">
          Реплик нет. Поставьте видео на нужный момент и нажмите «+ Реплика».
        </div>
      ) : (
        <div className="line-list">
          {sorted.map((line) => (
            <LineItem
              key={line.id}
              line={line}
              role={roleById.get(line.roleId)}
              roles={roles}
              takes={takeCountByLine[line.id] ?? 0}
              selected={line.id === selectedLineId}
              merging={mergeSelection ? mergeSelection.has(line.id) : undefined}
              onToggleMerge={onToggleMerge}
              autoFocus={autoFocusLineId === line.id}
              currentTime={currentTime}
              videoDuration={videoDuration}
              disabled={disabled}
              onSelect={onSelect}
              onSeek={onSeek}
              onPatch={onPatch}
              onDelete={onDelete}
              extra={line.id === selectedLineId && renderExtra ? renderExtra(line) : null}
            />
          ))}
        </div>
      )}
    </section>
  )
}
